/** ***
------------------------------------------------------------------------
HOST LIST PAGE
------------------------------------------------------------------------
  Host Name (editable by Admin, default - none)
  Host OS (Linux / Windows icons)
  Host OS Version
  Host Type (editable by Admin, Options: Critical, Regular, default - Regular)
  Local IP
  MAC Address
  Host Status
  Monitoring Since
  Last Seen
  Agent ID - (ID of the Confluera Agent)
  Agent Version
  Agent Status

** ***/
import { DATETIME_FORMAT } from '@/constants/datetime.constants'

const hostsDetailMetaInfo = {
  meta: {
    columns: [
      {
        datatype: 'expand',
        description: 'More Details',
        label: 'More Details',
        sortable: false,
        'min-width': 150,
        name: 'expand',
        component: 'ExpandVmDetails'
      },
      {
        datatype: 'selection',
        description: 'Select',
        label: 'Select',
        sortable: false,
        'min-width': 55,
        name: 'selection'
      },
      {
        datatype: 'string',
        description: 'User Defined Hostname',
        label: 'VM Name',
        display: 'link',
        sortable: true,
        'min-width': 150,
        name: 'customHostname',
        pathTo: 'host-insights',
        params: {
          name: 'agentId',
          keyLookup: 'agentId'
        }
      },
      {
        datatype: 'string',
        description: 'Source',
        label: 'Source',
        sortable: true,
        'min-width': 120,
        name: 'source',
        display: 'component',
        displayKey: [
          { key: 'AWS', icon: 'table-logos-icon logo-aws-color' },
          { key: 'AZURE', icon: 'table-logos-icon logo-only-azure-color' },
          { key: 'GCP', icon: 'table-logos-icon logo-only-gcp-color' },
          { key: 'CONFLUERA', icon: 'table-logos-icon logo-confluera-color' },
          { key: 'WEF', icon: 'table-logos-icon logo-wef-color' }
        ],
        component: 'DiscoverySourceTableCell',
        componentFields: {
          discoveryKey: 'discoveryAccountNickname'
        }
      },
      {
        datatype: 'string',
        description: 'Protected',
        label: 'Protected',
        sortable: true,
        'min-width': 100,
        name: 'protected',
        display: 'colorTag',
        displayKey: [
          {
            key: 'Yes',
            display: 'Yes',
            color: '#E8ECF4',
            textColor: '#4B4E54',
            icon: 'far fa-check',
            iconDirection: 'right'
          },
          {
            key: 'No',
            display: 'No Agent',
            color: '#FEE5EA',
            textColor: '#EE3354',
            icon: 'fal fa-exclamation-triangle',
            iconDirection: 'right'
          },
          {
            key: 'Offline',
            display: 'Offline',
            // color: '#77dab4',
            // textColor: '#008044',
            icon: 'far fa-check',
            iconDirection: 'right'
          },
          {
            key: 'Upgrade',
            display: 'Upgrade',
            color: '#DFF6ED',
            textColor: '#00A45F',
            icon: 'fal fa-exclamation-triangle',
            iconDirection: 'right'
          }
        ]
      },
      {
        datatype: 'string',
        description: 'Needs Attention',
        label: 'Needs Attention',
        name: 'needsAttention',
        sortable: true,
        'min-width': 150,
        display: 'component',
        component: 'NeedsAttentionTableCell',
        componentFields: {
          needsAttention: 'needsAttention',
          managed: 'managed',
          upgradeable: 'upgradeable',
          numProgressions: 'numProgressions',
          status: 'agentStatus',
          accountKey: 'agentId',
          nickName: 'customHostname'
        }
      },
      {
        datatype: 'number',
        description: 'Progressions',
        label: 'Progressions',
        sortable: true,
        'min-width': 150,
        name: 'numProgressions',
        display: 'link',
        pathTo: 'account-progressions',
        query: [
          {
            name: 'accountKey',
            keyLookup: 'agentId'
          },
          {
            name: 'nickName',
            keyLookup: 'customHostname'
          }
        ]
      },
      {
        datatype: 'number',
        description: 'Detections',
        label: 'Detections',
        sortable: true,
        name: 'numDetections',
        'min-width': 125,
        display: 'link',
        pathTo: 'account-iocs',
        query: [
          {
            name: 'accountKey',
            keyLookup: 'agentId'
          },
          {
            name: 'nickName',
            keyLookup: 'customHostname'
          }
        ]
      },
      {
        datatype: 'objectpath',
        description: 'Exposed',
        label: 'Exposed',
        sortable: true,
        'min-width': 100,
        name: 'agentDetails.openToPublicInternet',
        display: 'colorTag',
        displayKey: [
          {
            key: true,
            display: 'Yes',
            color: '#FEE5EA',
            textColor: '#EE3354'
          },
          {
            key: false,
            display: 'No',
            color: '#E8ECF4',
            textColor: '#4B4E54'
          },
          {
            key: 'n/a',
            display: 'N/A',
            color: '#E8ECF4',
            textColor: '#4B4E54'
          }
        ]
      },
      {
        datatype: 'objectpath',
        description: 'Agent Version',
        label: 'Agent Version',
        sortable: true,
        name: 'agentInfo.agentVersion',
        'min-width': 150
      },
      {
        datatype: 'array',
        description: 'Business Tags',
        label: 'Business Tags',
        visible: false,
        sortable: true,
        name: 'tags',
        'min-width': 150
      },
      {
        datatype: 'runtime_string',
        visible: false,
        format: DATETIME_FORMAT,
        description: 'Running Time',
        label: 'Running',
        name: 'upTime',
        'min-width': 150,
        sortable: true
      },
      {
        datatype: 'string',
        visible: false,
        description: 'Location',
        label: 'Location',
        name: 'location',
        'min-width': 150,
        sortable: true
      },
      {
        datatype: 'string',
        description: 'Instance Id',
        label: 'Instance Id',
        visible: false,
        name: 'instanceId'
      },
      {
        datatype: 'objectpath',
        description: 'Vpc',
        label: 'Vpc',
        visible: false,
        name: 'agentDetails.vpcId'
      },
      {
        datatype: 'objectpath',
        description: 'Region',
        label: 'Region',
        visible: false,
        name: 'agentDetails.region'
      },
      {
        datatype: 'objectpath',
        description: 'Security Group(s)',
        label: 'Security Group(s)',
        visible: false,
        name: 'agentDetails.securityGroupsList'
      },
      {
        datatype: 'ip_address',
        description: 'Public IP',
        label: 'Public IP',
        visible: false,
        name: 'publicIp',
        'min-width': 150,
        sortable: true
      },
      {
        datatype: 'ip_address',
        description: 'Private IP',
        label: 'Private IP',
        visible: false,
        name: 'privateIp',
        'min-width': 150,
        sortable: true
      },
      {
        datatype: 'string',
        description: 'OS',
        label: 'OS',
        visible: false,
        name: 'os',
        sortable: true
      },
      {
        datatype: 'string',
        visible: false,
        description: 'Discovered',
        label: 'Discovered',
        name: 'discovered',
        'min-width': 150,
        sortable: true
      },
      {
        datatype: 'string',
        visible: false,
        description: 'Nickname',
        label: 'Nickname',
        name: 'discoveryAccountNickname',
        'min-width': 150,
        sortable: true
      }
    ]
  }
}

export { hostsDetailMetaInfo }



// WEBPACK FOOTER //
// ./src/column-header/hosts/hosts-details-meta.js